import type { ReactNode } from "react";
import { Link } from "react-router";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { Bento, PageRefusal } from "@/components/ui/box";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, Read } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Button } from "@/components/ui/button";
import {
  boxRead,
  imam,
  resultState,
  useSettingsCommit,
  type PendingSettingsSave,
} from "@/components/settings/settings-boxes";
import { formatTimestamp } from "@/components/settings/settings-copy";
import {
  integrationWords as w,
  zoomActionWords,
} from "@/components/settings/integrations-copy";
import { useWorkspaceEyebrow } from "@/hooks/use-workspace-eyebrow";
import { ApiError } from "@/lib/api/client";
import {
  integrationSettingsApi as service,
  type GoogleDiagnostics,
  type IntegrationOverview,
  type ZoomAction,
  type ZoomManagement,
} from "@/lib/api/integration-settings";
import { googleDrivePickerConfigForHostname } from "@/lib/google-drive-picker-config";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { driveWords } from "@/components/drive/course-drive-copy";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

function read<T>(query: UseQueryResult<T>): Read<T> {
  if (query.error instanceof ApiError && query.error.status === 403)
    return { status: "forbidden" };
  return boxRead(query, w.failed);
}
function Facts({
  values,
}: {
  values: Array<[string, string | boolean | null]>;
}) {
  return (
    <dl className="grid gap-3 text-ui sm:grid-cols-2">
      {values.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="break-all font-medium">
            {typeof value === "boolean"
              ? value
                ? w.yes
                : w.no
              : value || w.noValue}
          </dd>
        </div>
      ))}
    </dl>
  );
}
function fact<T>(
  id: string,
  label: string,
  query: UseQueryResult<T>,
  prose: (value: T) => string,
  body: (value: T) => ReactNode,
  detail = "",
): Box {
  return {
    id,
    label,
    job: label,
    kind: "fact",
    owner: imam,
    read: read(query),
    readTime: true,
    fold: "never",
    detail: () => detail,
    prose: (value) => prose(value as T),
    body: (value) => body(value as T),
  };
}
const config = (configured: boolean, enabled = true, supported = true) =>
  !supported
    ? w.unsupported
    : !configured
      ? w.notConfigured
      : enabled
        ? w.configured
        : w.disabled;

function useZoomTask(
  query: UseQueryResult<ZoomManagement>,
  allowed: boolean,
): Box {
  const snapshot = query.data;
  const keep = useTabDraft<
    { action: ZoomAction; userId: string; zoomId: string; reason: string },
    PendingSettingsSave
  >({
    box: "zoom-management",
    record: "institution",
    version: snapshot?.version ?? null,
    initial: { action: "verify_account", userId: "", zoomId: "", reason: "" },
  });
  const refresh = async () => {
    await query.refetch();
  };
  const outcome = useSettingsCommit({
    keep,
    refresh,
    versionField: "version",
    onChecked: keep.end,
  });
  const { action, userId, zoomId, reason } = keep.value;
  const hostAction = action === "verify_host" || action === "revoke_host";
  const verifying = action === "verify_account" || action === "verify_host";
  const revoking = action === "revoke_account" || action === "revoke_host";
  const hosts = snapshot?.hosts ?? [];
  const change = (field: keyof typeof keep.value, value: string) => {
    outcome.reset();
    keep.set((was) => ({ ...was, [field]: value }));
  };
  const blockers = (): Blocker[] => {
    const reasons: string[] = [];
    if (!snapshot || query.isError) reasons.push(w.reload);
    if (keep.conflict) reasons.push(w.conflict);
    if (
      hostAction &&
      !hosts.some(
        (h) =>
          h.user_id === userId &&
          (action === "revoke_host"
            ? h.host_binding_id && !h.revoked_at
            : h.is_active && ["admin", "instructor"].includes(h.role)),
      )
    )
      reasons.push(w.chooseHost);
    if (
      verifying &&
      (!zoomId.trim() || /\s|@/.test(zoomId) || zoomId.length > 255)
    )
      reasons.push(w.enterId);
    if (revoking && (!reason.trim() || reason.length > 500))
      reasons.push(w.enterReason);
    if (
      action !== "verify_account" &&
      (!snapshot?.connection || snapshot.connection.revoked_at)
    )
      reasons.push(w.accountNeeded);
    if (action === "enable" && !allowed) reasons.push(w.releaseBlocked);
    return reasons.map((sentence) => ({
      kind: "missing",
      part: "command",
      sentence,
    }));
  };
  return {
    id: "zoom-management",
    label: w.manage,
    job: w.manage,
    kind: "task",
    owner: imam,
    read: keep.settled ? read(query) : { status: "loading" },
    fold: "never",
    draft: keep.kept,
    detail: () => w.reviewAction,
    candidate: { eligible: true, blocked: blockers()[0] ?? null, rank: null },
    state: () =>
      resultState(outcome.result, {
        class: "quiet",
        word: zoomActionWords[action],
      }),
    result: outcome.result,
    parts: [
      {
        id: "command",
        label: w.action,
        owner: imam,
        fold: "never",
        draft: "browser",
        markedByBox: true,
        state: { class: "quiet", word: w.reviewAction },
        render: () => (
          <div className="grid gap-4">
            {keep.conflict && (
              <div role="alert">
                <p>{w.conflict}</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    keep.loadNewer();
                    outcome.reset();
                  }}
                >
                  {w.reload}
                </Button>
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="zoom-action">{w.action}</Label>
              <NativeSelect
                id="zoom-action"
                value={action}
                onChange={(event) => change("action", event.target.value)}
              >
                {Object.entries(zoomActionWords).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {hostAction && (
              <div className="grid gap-2">
                <Label htmlFor="zoom-host">{w.host}</Label>
                <NativeSelect
                  id="zoom-host"
                  value={userId}
                  onChange={(event) => change("userId", event.target.value)}
                >
                  <option value="">{w.chooseHost}</option>
                  {hosts
                    .filter((host) =>
                      action === "revoke_host"
                        ? host.host_binding_id && !host.revoked_at
                        : host.is_active &&
                          ["admin", "instructor"].includes(host.role),
                    )
                    .map((host) => (
                      <option key={host.user_id} value={host.user_id}>
                        {host.display_name}
                      </option>
                    ))}
                </NativeSelect>
              </div>
            )}
            {verifying && (
              <div className="grid gap-2">
                <Label htmlFor="zoom-user-id">{w.zoomId}</Label>
                <Input
                  id="zoom-user-id"
                  value={zoomId}
                  maxLength={255}
                  autoComplete="off"
                  aria-describedby="zoom-id-help"
                  onChange={(event) => change("zoomId", event.target.value)}
                />
                <p
                  id="zoom-id-help"
                  className="text-meta text-muted-foreground"
                >
                  {w.idHelp}
                </p>
              </div>
            )}
            {revoking && (
              <div className="grid gap-2">
                <Label htmlFor="zoom-reason">{w.reason}</Label>
                <Input
                  id="zoom-reason"
                  value={reason}
                  maxLength={500}
                  onChange={(event) => change("reason", event.target.value)}
                />
              </div>
            )}
            <p className="text-ui text-muted-foreground">
              {w.unchangedMeetings}
            </p>
          </div>
        ),
      },
    ],
    commit: {
      verb: w.apply,
      cap: zoomActionWords[action],
      effects: () => [{ id: action, sentence: zoomActionWords[action] }],
      blockers,
      run: async (key) => {
        if (!snapshot || blockers().length) throw new Error(w.reload);
        const result = await outcome.run(
          key,
          (id) =>
            service.manageZoom(
              {
                action,
                expected_version: keep.began ?? snapshot.version,
                ...(hostAction ? { user_id: userId } : {}),
                ...(verifying ? { zoom_user_id: zoomId } : {}),
                ...(revoking ? { reason: reason.trim() } : {}),
              },
              id,
            ),
          [w.saved],
          refresh,
        );
        if (result.state === "confirmed") {
          keep.end();
          await refresh();
        } else if (result.state === "refused") await refresh();
        return result;
      },
    },
  };
}

export function IntegrationsPage() {
  const { me } = useAuth();
  const eyebrow = useWorkspaceEyebrow();
  const overview = useQuery({
    queryKey: ["admin", "integrations"],
    queryFn: service.overview,
    retry: false,
  });
  const google = useQuery({
    queryKey: ["admin", "integrations", "google"],
    queryFn: service.google,
    retry: false,
  });
  const supported = Boolean(
    overview.data?.zoom.supported && overview.data.zoom.configured,
  );
  const zoom = useQuery({
    queryKey: ["admin", "integrations", "zoom"],
    queryFn: service.zoom,
    enabled: supported,
    retry: false,
  });
  const task = useZoomTask(zoom, Boolean(overview.data?.zoom.creation_allowed));
  const picker = googleDrivePickerConfigForHostname();
  const googleFact = (
    id: string,
    label: string,
    prose: (value: GoogleDiagnostics) => string,
    values: (
      value: GoogleDiagnostics,
    ) => Array<[string, string | boolean | null]>,
    help: string,
    extra?: ReactNode,
  ) =>
    fact(id, label, google, prose, (value) => (
      <div className="grid gap-4">
        <Facts values={values(value)} />
        <p className="text-ui text-muted-foreground">{help}</p>
        {extra}
      </div>
    ));
  const boxes: Box[] = [
    fact<GoogleDiagnostics>(
      "google-overview",
      w.google,
      google,
      (value) => value.domain ?? w.notConfigured,
      () => (
        <p className="text-ui text-muted-foreground">
          {w.diagnostics} {w.providerSetup}
        </p>
      ),
    ),
    googleFact(
      "google-signin",
      w.signin,
      (value) => config(value.signin.configured),
      (value) => [[w.issuer, value.signin.issuer]],
      w.signinHelp,
    ),
    googleFact(
      "google-directory",
      w.directory,
      (value) =>
        config(
          value.directory.configured,
          value.directory.provisioning_enabled,
        ),
      (value) => [
        [w.provisioning, value.directory.provisioning_enabled],
        [w.orgUnit, value.directory.org_unit],
      ],
      w.directoryHelp,
    ),
    fact(
      "google-picker",
      w.picker,
      google,
      () => (picker.enabled ? w.pickerReady : w.pickerOff),
      () => <p className="text-ui text-muted-foreground">{w.pickerHelp}</p>,
    ),
    googleFact(
      "google-drive",
      w.drive,
      (value) =>
        config(
          value.drive.configured && value.drive.operator_matches_domain,
          value.drive.enabled,
        ),
      (value) => [
        [w.managedCreation, value.drive.enabled],
        [w.operator, value.drive.operator],
        [w.root, value.drive.root_id],
        [w.domainMatch, value.drive.operator_matches_domain],
      ],
      w.driveHelp,
    ),
    googleFact(
      "google-classroom",
      w.classroom,
      (value) =>
        config(
          value.classroom.configured,
          value.classroom.intake_enabled,
          value.classroom.supported,
        ),
      (value) => [
        [w.intakeEnabled, value.classroom.intake_enabled],
        [w.references, value.classroom.references_enabled],
        [
          w.connectionState,
          value.classroom.connection?.revoked_at
            ? w.revoked
            : !value.classroom.connection
              ? w.noConnection
              : value.classroom.connection.grant_state === "active" &&
                  value.classroom.connection.client_current &&
                  !value.classroom.connection.scopes_missing.length
                ? w.activeGrant
                : w.reconnect,
        ],
        [w.subject, value.classroom.connection?.provider_user_id ?? null],
        [
          w.scopes,
          value.classroom.connection
            ? !value.classroom.connection.scopes_missing.length
            : null,
        ],
        [w.currentClient, value.classroom.connection?.client_current ?? null],
      ],
      w.classroomHelp,
      google.data?.classroom.supported &&
        google.data.classroom.intake_enabled ? (
        <Link
          className="text-ui font-semibold text-primary"
          to={PROTECTED_ROUTES.admin.classroomIntake}
        >
          {w.intake}
        </Link>
      ) : undefined,
    ),
    googleFact(
      "google-meet",
      w.meet,
      (value) =>
        config(value.meet.configured, value.meet.enabled, value.meet.supported),
      (value) => [
        [w.configuredLabel, value.meet.configured],
        [w.managedCreation, value.meet.enabled],
      ],
      w.meetHelp,
    ),
    fact<IntegrationOverview>(
      "zoom-overview",
      w.zoom,
      overview,
      (value) =>
        config(
          value.zoom.configured,
          value.zoom.creation_allowed,
          value.zoom.supported,
        ),
      (value) => (
        <div className="grid gap-4">
          <Facts
            values={[
              [w.configuredLabel, value.zoom.configured],
              [w.recordingConfig, value.zoom.recordings_configured],
              [w.recordings, value.zoom.recordings_enabled],
            ]}
          />
          <p className="text-ui text-muted-foreground">
            {w.zoomHelp} {w.recordingHelp}
          </p>
          <Link
            className="text-ui font-semibold text-primary"
            to={PROTECTED_ROUTES.admin.settings}
          >
            {w.recordingSettings}
          </Link>
        </div>
      ),
      w.diagnostics,
    ),
  ];
  if (supported)
    boxes.push(
      fact<ZoomManagement>(
        "zoom-account",
        w.account,
        zoom,
        (value) =>
          !value.connection
            ? w.accountAbsent
            : value.connection.revoked_at
              ? w.revoked
              : value.connection.enabled
                ? w.enabled
                : w.stopped,
        (value) => (
          <Facts
            values={[
              [w.accountId, value.connection?.account_id ?? null],
              [
                w.verifiedAt,
                value.connection?.verified_at
                  ? formatTimestamp(value.connection.verified_at)
                  : null,
              ],
            ]}
          />
        ),
      ),
      {
        id: "zoom-hosts",
        label: w.hosts,
        job: w.hosts,
        kind: "list",
        owner: imam,
        read: read(zoom),
        readTime: true,
        fold: "never",
        detail: () => w.idHelp,
        state: () => ({ class: "quiet", word: w.hosts }),
        rows: (value) => (value as ZoomManagement).hosts,
        row: {
          key: (value) => (value as ZoomManagement["hosts"][number]).user_id,
          title: (value) =>
            (value as ZoomManagement["hosts"][number]).display_name,
          facts: (value) => {
            const host = value as ZoomManagement["hosts"][number];
            return [
              !host.is_active
                ? w.inactive
                : !["admin", "instructor"].includes(host.role)
                  ? w.roleEnded
                  : host.revoked_at
                    ? w.revoked
                    : !host.host_binding_id
                      ? w.noBinding
                      : host.license_state === "licensed"
                        ? w.licensed
                        : host.license_state === "unlicensed"
                          ? w.unlicensed
                          : w.unknownLicense,
              ...(host.zoom_user_id
                ? [`${w.providerId}: ${host.zoom_user_id}`]
                : []),
              ...(host.verified_at
                ? [`${w.verifiedAt}: ${formatTimestamp(host.verified_at)}`]
                : []),
            ].join(" · ");
          },
        },
      },
      task,
    );
  if (overview.error instanceof ApiError && overview.error.status === 403)
    return (
      <PageRefusal
        id="integrations"
        journey="integrations"
        refusal={{
          reason: "forbidden",
          sentence: w.failed,
          back: { label: w.settings, to: PROTECTED_ROUTES.admin.settings },
        }}
      />
    );
  return (
    <div className="app-page">
      <AcademicPageHeader
        eyebrow={eyebrow}
        title={w.title}
        description={w.description}
      />
      <Link
        to={PROTECTED_ROUTES.admin.settings}
        className="text-ui font-semibold text-primary"
      >
        {w.settings}
      </Link>
      <Button
        className="justify-self-start"
        variant="outline"
        onClick={() => {
          void overview.refetch();
          void google.refetch();
          if (supported) void zoom.refetch();
        }}
      >
        {w.refresh}
      </Button>
      {me?.workspaces?.teaching.available && <Card><CardHeader><CardTitle>{driveWords.title}</CardTitle><CardDescription>{driveWords.intro}</CardDescription></CardHeader><CardContent><Link className="text-ui font-semibold text-primary" to={PROTECTED_ROUTES.adminTeaching.drive}>{driveWords.openWorkspace}</Link></CardContent></Card>}
      <Bento id="integrations" journey="integrations" boxes={boxes} />
    </div>
  );
}
